import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

Modal.setAppElement('#root');

const Customers = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    paymentTerms: 'Net 30',
    userId: user?.uid || '',
    active: true
  });
  const [editingCustomer, setEditingCustomer] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(true);
  const [hasFetchedCustomers, setHasFetchedCustomers] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const customersCollectionRef = collection(db, "customers");
  const accountsCollectionRef = collection(db, "accounts"); // For future default income account matching

  useEffect(() => {
    if (user && userData) {
      fetchCustomers();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = customers.filter((customer) =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    if (!user?.uid) return;
    
    setIsFetchingCustomers(true);
    try {
      const q = query(customersCollectionRef);
      const data = await getDocs(q);
      const customerList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setCustomers(customerList);
      setHasFetchedCustomers(true);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setError("Failed to fetch customers");
      setShowModal(true);
    } finally {
      setIsFetchingCustomers(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer({ ...newCustomer, [name]: value });
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      setError("Customer name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(customersCollectionRef, {
        ...newCustomer,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Customer added successfully");
      fetchCustomers();
      resetNewCustomer();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add customer");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateCustomer = async () => {
    setUpdate(true);
    try {
      const customerDoc = doc(db, "customers", editingCustomer.id);
      await updateDoc(customerDoc, newCustomer);
      setSuccessMessage("Customer updated successfully");
      fetchCustomers();
      resetNewCustomer();
      setEditingCustomer(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update customer");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetNewCustomer = () => {
    setNewCustomer({
      name: '',
      contactEmail: '',
      contactPhone: '',
      paymentTerms: 'Net 30',
      userId: user?.uid || '',
      active: true
    });
  };

  const handleEditCustomer = (customerToEdit) => {
    setEditingCustomer(customerToEdit);
    setNewCustomer({
      name: customerToEdit.name,
      contactEmail: customerToEdit.contactEmail || '',
      contactPhone: customerToEdit.contactPhone || '',
      paymentTerms: customerToEdit.paymentTerms,
      userId: customerToEdit.userId,
      active: customerToEdit.active
    });
    setIsEditModalOpen(true);
  };

  const confirmDeleteCustomer = (customerToDelete) => {
    setCurrentCustomer(customerToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCustomer = async (id) => {
    setUpdate(true);
    try {
      const customerDoc = doc(db, "customers", id);
      await deleteDoc(customerDoc);
      setSuccessMessage("Customer deleted successfully");
      fetchCustomers();
    } catch (error) {
      setError("Failed to delete customer");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
    setFile(null);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !email || re.test(email);
  };

  const handleBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setImportStatus('Processing file...');
    setImportProgress(0);

    try {
      let customersData = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        customersData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        customersData = result.data;
      }

      if (customersData.length === 0) {
        throw new Error("No customer data found in the file");
      }

      setImportStatus(`Importing ${customersData.length} customers...`);

      let successCount = 0;
      let errorCount = 0;
      const validPaymentTerms = ['Net 30', 'Net 15', 'Due on Receipt', 'Custom'];

      for (let i = 0; i < customersData.length; i++) {
        try {
          const customer = customersData[i];

          if (!customer.name?.trim()) {
            errorCount++;
            continue;
          }

          const contactEmail = customer.contactEmail?.trim() || '';
          if (contactEmail && !validateEmail(contactEmail)) {
            errorCount++;
            continue;
          }

          const paymentTerms = customer.paymentTerms?.trim() || 'Net 30';
          if (!validPaymentTerms.includes(paymentTerms)) {
            errorCount++;
            continue;
          }

          const customerData = {
            name: customer.name.trim(),
            contactEmail,
            contactPhone: customer.contactPhone?.trim() || '',
            paymentTerms,
            userId: user?.uid,
            active: true,
            createdAt: Timestamp.now()
          };

          await addDoc(customersCollectionRef, customerData);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing customer ${i + 1}:`, error);
        }

        setImportProgress(Math.round(((i + 1) / customersData.length) * 100));
      }

      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} customers added successfully`);
      fetchCustomers();
    } catch (error) {
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingCustomers && !hasFetchedCustomers) {
      return <p className="loading-text">Loading customers...</p>;
    }
    
    if (filteredCustomers.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No customers match your search.</p>;
      } else {
        return <p className="loading-text">No customers found. Add your first customer.</p>;
      }
    }
    
    return filteredCustomers.map((customerItem) => (
      <div key={customerItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{customerItem.name}</strong><br />
            <span>Phone: {customerItem.contactPhone}</span><br />
            <span>Email: {customerItem.contactEmail}</span><br />           
            <span>Terms: {customerItem.paymentTerms}</span><br />
            <span className="size5 font-light-blue-cash bold">{customerItem.active ? 'Active' : 'Inactive'}</span>
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditCustomer(customerItem)}>
            <i className="fas fa-edit"></i>
          </button>
          <button className="list-btn delete-btn" onClick={() => confirmDeleteCustomer(customerItem)}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Customers</h2>
            <Link to="#" className="home-link" onClick={() => fetchCustomers()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Customer</button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Customer Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Customer"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Customer</h2>
        <form className="session-form">
          <label htmlFor="name" className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Customer Name"
            value={newCustomer.name}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="contactEmail" className="input-label">Email</label>
          <input
            type="email"
            name="contactEmail"
            className="input-field"
            placeholder="Contact Email"
            value={newCustomer.contactEmail}
            onChange={handleInputChange}
          />
          <label htmlFor="contactPhone" className="input-label">Phone</label>
          <input
            type="text"
            name="contactPhone"
            className="input-field"
            placeholder="Contact Phone"
            value={newCustomer.contactPhone}
            onChange={handleInputChange}
          />
          <label htmlFor="paymentTerms" className="input-label">Payment Terms</label>
          <select
            name="paymentTerms"
            className="input-field"
            value={newCustomer.paymentTerms}
            onChange={handleInputChange}
          >
            <option value="Net 30">Net 30</option>
            <option value="Net 15">Net 15</option>
            <option value="Due on Receipt">Due on Receipt</option>
            <option value="Custom">Custom</option>
          </select>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddCustomer}>Add Customer</button>
          </div>
        </form>
      </Modal>

      {/* Edit Customer Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewCustomer();
          setEditingCustomer(null);
        }}
        contentLabel="Edit Customer"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Customer</h2>
        <form className="session-form">
          <label htmlFor="name" className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Customer Name"
            value={newCustomer.name}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="contactEmail" className="input-label">Email</label>
          <input
            type="email"
            name="contactEmail"
            className="input-field"
            placeholder="Contact Email"
            value={newCustomer.contactEmail}
            onChange={handleInputChange}
          />
          <label htmlFor="contactPhone" className="input-label">Phone</label>
          <input
            type="text"
            name="contactPhone"
            className="input-field"
            placeholder="Contact Phone"
            value={newCustomer.contactPhone}
            onChange={handleInputChange}
          />
          <label htmlFor="paymentTerms" className="input-label">Payment Terms</label>
          <select
            name="paymentTerms"
            className="input-field"
            value={newCustomer.paymentTerms}
            onChange={handleInputChange}
          >
            <option value="Net 30">Net 30</option>
            <option value="Net 15">Net 15</option>
            <option value="Due on Receipt">Due on Receipt</option>
            <option value="Custom">Custom</option>
          </select>
          <label>
            <input
              type="checkbox"
              name="active"
              checked={newCustomer.active}
              onChange={(e) => setNewCustomer({ ...newCustomer, active: e.target.checked })}
            />
            Active
          </label>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewCustomer();
              setEditingCustomer(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateCustomer}>Update Customer</button>
          </div>
        </form>
      </Modal>

      {/* Delete Customer Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Customer"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Customer</h2>
        {currentCustomer && (
          <div>
            <p>Are you sure you want to delete <strong>{currentCustomer.name}</strong>? This action cannot be undone.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteCustomer(currentCustomer.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Customers"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Customers</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label style={{ marginLeft: '15px' }}>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div style={{ marginTop: '10px' }}>
              <p>{importStatus}</p>
              {importProgress > 0 && importProgress < 100 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15" style={{ marginTop: '15px' }}>
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={handleBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions" style={{ marginTop: '20px', fontSize: '12px' }}>
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> name</p>
            <p><strong>OPTIONAL:</strong> contactEmail, contactPhone, paymentTerms</p>
            <p><strong>VALID PAYMENT TERMS:</strong> Net 30, Net 15, Due on Receipt, Custom</p>
            <p><strong>Excel/CSV:</strong> First row must be headers</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Customers;